import { useEffect, useRef, useState } from 'react';

// Types
import type { AxiosError } from 'axios';
import { dequal } from 'dequal';
import useSWR from 'swr';

// Utils
import { get } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';

import {
    GroupAnalyticsDataApiAnalyticsType,
    GroupAnalyticsDataApiType,
    GroupAnalyticsFieldsType,
    UseGroupAnalyticsProps
} from '@/utils/hooks/swr/group/useGroupAnalytics/types';

// Api Routes
import { GROUP_ANALYTICS } from '@/routes/apiEndpoints';

const useGroupAnalytics = <TFields extends GroupAnalyticsFieldsType[]>({
    activeGroupID,
    params,
    options
}: UseGroupAnalyticsProps<TFields>) => {
    const firstLoad = useRef(null);
    const [state, setState] = useState<Prettify<GroupAnalyticsDataApiAnalyticsType<TFields>>>(null);
    const [mostEfficientSocialProfiles, setMostEfficientSocialProfiles] = useState({});
    const [shouldReFetch, setShouldReFetch] = useState(false);

    if (params?.period !== 'custom') {
        delete params?.start;
        delete params?.end;
    }

    const [requestParams, setRequestParams] = useState(params);
    const isParamsIdentical = dequal(params, requestParams);

    const {
        data,
        mutate: mutateGroupAnalytics,
        isValidating: isValidatingAnalytics,
        error: groupAnalyticsError
    } = useSWR<GroupAnalyticsDataApiType<TFields>, AxiosError<Error>>(
        //the second part of this is needed to ensure that no unnecessary request is sent
        requestParams && (isParamsIdentical || shouldReFetch)
            ? [
                  injectEndpointUrlParams(GROUP_ANALYTICS, {
                      GROUP_ID: activeGroupID
                  }),
                  ...Object.values(requestParams)
              ]
            : null,
        ([url]) => get(url, requestParams).then((res) => res.data),
        {
            shouldRetryOnError: false,
            ...options
        }
    );

    useEffect(() => {
        if (isParamsIdentical) {
            // switch back shouldRefresh to false
            if (shouldReFetch) return setShouldReFetch(false);
            return;
        }
        setRequestParams(params);
        firstLoad.current = false;
    }, [activeGroupID, params?.period, params?.start, params?.end]);

    // setShouldReFetch based on requestParams
    useEffect(() => {
        if (isParamsIdentical) {
            // switch back shouldRefresh to false
            if (shouldReFetch) return setShouldReFetch(false);
            return;
        }
        //make sure we don't set shouldReFetch if it's not needed
        if (shouldReFetch) return;
        setShouldReFetch(true);
    }, [requestParams]);

    useEffect(() => {
        if (!data?.analytics && !groupAnalyticsError) {
            return;
        }
        firstLoad.current = true;
        setState(data?.analytics);
        setMostEfficientSocialProfiles(data?.social_profiles_performance_by_fields);
    }, [groupAnalyticsError, data?.analytics]);

    return {
        groupAnalytics: state,
        mostEfficientSocialProfiles,
        mutateGroupAnalytics,
        groupAnalyticsError,
        setRequestParams,
        setState,
        isValidatingAnalytics,
        isLoading: (!state && !groupAnalyticsError) || !firstLoad.current
    };
};
export default useGroupAnalytics;
